/**
 * The compact profile card — Teams' "who is this and can I reach them" panel.
 *
 * Mounted only while the card is open, which is what makes it affordable to
 * hang one off every avatar in a timeline: the two queries behind it do not
 * exist until someone actually clicks.
 */
import { Avatar, Skeleton, cn } from '@cyphex/ui';
import type { UserId } from '@cyphex/types';
import { ACTIVITY_META } from '@/features/presence/presenceMeta';
import { PresenceLine } from '@/features/presence/components/PresenceLine';
import { usePersonPresence, usePersonSnapshot } from '@/features/presence/hooks/usePresence';
import { useMe } from '@/features/chat/hooks/useMe';
import { usePersonProfile } from '../hooks/usePersonProfile';
import { ProfileAvailability } from './ProfileAvailability';
import { ProfileContact } from './ProfileContact';
import { ProfileQuickActions } from './ProfileQuickActions';
import { ProfileQuickMessage } from './ProfileQuickMessage';

export interface ProfileCardProps {
  userId: UserId;
  /** Name already on screen — shown while the profile is still in flight. */
  fallbackName?: string;
  fallbackAvatarUrl?: string | null;
  /** Closes the surrounding popover once an action has navigated away. */
  onDismiss?: () => void;
  className?: string;
}

function ProfileCardSkeleton({ name }: { name: string }) {
  return (
    <div className="space-y-3" aria-busy="true">
      <div className="flex items-center gap-3">
        <Skeleton shape="circle" className="h-14 w-14 shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="truncate text-lg font-semibold text-foreground">{name}</p>
          <Skeleton className="h-3 w-28" />
        </div>
      </div>
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-3 w-40" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

export function ProfileCard({
  userId,
  fallbackName = 'Loading…',
  fallbackAvatarUrl,
  onDismiss,
  className,
}: ProfileCardProps) {
  const me = useMe();
  const { user, availability, isLoading, isError } = usePersonProfile(userId);
  const activity = usePersonPresence(userId);
  /* The same subscription, one level deeper: `usePersonPresence` collapses a
     person to a single state, and "when were they last here" is the one thing
     the card needs that the state alone cannot say. */
  const snapshot = usePersonSnapshot(userId);
  const isSelf = me?.id === userId;

  if (isLoading) {
    return (
      <div className={cn('w-80 p-4', className)}>
        <ProfileCardSkeleton name={fallbackName} />
      </div>
    );
  }

  // A failed profile fetch must not pretend. The name we already had on screen
  // is true; everything else the card would claim is not, so it says so.
  if (user === null) {
    return (
      <div className={cn('w-80 space-y-2 p-4', className)}>
        <p className="text-lg font-semibold text-foreground">{fallbackName}</p>
        <p className="text-md text-muted-foreground">
          {isError ? 'This profile could not be loaded.' : 'No profile available.'}
        </p>
      </div>
    );
  }

  return (
    <div className={cn('w-80 space-y-4 p-4', className)}>
      <header className="flex items-start gap-3">
        <Avatar
          name={user.displayName}
          src={user.avatarUrl ?? fallbackAvatarUrl ?? undefined}
          size="2xl"
          presence={ACTIVITY_META[activity].dot}
          /* The `PresenceLine` below says "Presenting"; without this the dot
             beside it announced "Do not disturb" — two answers to one question
             about the same person, in the same header. */
          presenceLabel={ACTIVITY_META[activity].label}
          presenceRingClassName="ring-popover"
        />
        <div className="min-w-0 flex-1 pt-0.5">
          <h3 className="break-words text-lg font-semibold leading-tight text-foreground">
            {user.displayName}
          </h3>
          {user.jobTitle === null || user.jobTitle === '' ? null : (
            <p className="mt-0.5 break-words text-md text-muted-foreground">{user.jobTitle}</p>
          )}
          {/* The state in words, plus "last seen" when the person is not here.
              The dot on the avatar carries the same fact in colour, which is
              exactly why this line exists: Presenting and Do not disturb share
              a badge, and an amber dot cannot say whether somebody stepped out
              a minute ago or on Friday. */}
          <PresenceLine
            state={activity}
            lastActivityAt={snapshot?.resolved === true ? snapshot.lastActivityAt : null}
          />
        </div>
      </header>

      <ProfileQuickActions
        userId={userId}
        displayName={user.displayName}
        isSelf={isSelf}
        {...(onDismiss === undefined ? {} : { onNavigate: onDismiss })}
      />

      {isSelf ? null : (
        <ProfileQuickMessage
          userId={userId}
          displayName={user.displayName}
          {...(onDismiss === undefined ? {} : { onSent: onDismiss })}
        />
      )}

      <ProfileAvailability
        userId={userId}
        timezone={user.timezone}
        availability={availability}
      />

      <ProfileContact user={user} />
    </div>
  );
}
